import React, { useMemo, useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../PaymentFormManualOrder";
import { License } from "types";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { getAuthCookie } from "../../utils/authCookies";
export type PaymentMethod = "card" | "bank" | "cheque" | "store_paid";

/** Split a partial batch total across orders proportionally (last order gets rounding remainder). */
function distributePartialAcrossOrders(
  orders: SelectedOrder[],
  amountToPay: number
): SelectedOrder[] {
  if (!orders.length || amountToPay <= 0) return orders;

  const balances = orders.map((o) => Number(o.amount || 0));
  const totalBalance = balances.reduce((sum, n) => sum + n, 0);
  if (totalBalance <= 0) return orders;

  if (amountToPay >= totalBalance - 0.0001) {
    return orders.map((o, i) => ({ ...o, amount: balances[i] }));
  }

  let allocatedSoFar = 0;
  const amounts: number[] = [];

  for (let i = 0; i < orders.length; i++) {
    if (i === orders.length - 1) {
      amounts.push(Number((amountToPay - allocatedSoFar).toFixed(2)));
      break;
    }
    const share = (balances[i] / totalBalance) * amountToPay;
    const amt = Number(Math.min(balances[i], share).toFixed(2));
    amounts.push(amt);
    allocatedSoFar += amt;
  }

  return orders.map((o, i) => ({ ...o, amount: amounts[i] }));
}

interface SelectedOrder {
  order_id: string;
  order_type: string;
  order_number?: string;
  bill_to?: string;
  amount?: number;
}

interface PaymentSectionProps {
  selectedCount: number;
  selectedBalance: number;
  selectedOrders: SelectedOrder[];
  allocatedAmount?: number;
  remainingAmount?: number;
  defaultEmailReceipt?: boolean;
  defaultMethod?: PaymentMethod;
  selectedVendor?: { supplier_id: string; name: string; email: string; linked_at: string } | null;
  // New props for batch payment payload
  scope?: string;
  company?: string;
  teamMembers?: string[];
  offices?: string[];
  agents?: string[];
  bill_from?: string;
  bill_to?: string;
  /** When set (e.g. from Batch Payment Report), included in POST /batch-payments/payments */
  reportId?: string;
  /**
   * UI layout:
   * - default: create batch payment (shows selected order count + balance)
   * - report: report page payment (hides selection summary; wider method/memo/stripe)
   */
  variant?: "default" | "report";
  /** Public pay link: only Card (hide bank/cheque/store paid) */
  cardOnly?: boolean;
  /** Max collectable amount (outstanding balance on report page) */
  maxAllowed?: number;
  /** Report page: signage / signageteammember only — set by BatchPaymentReport */
  allowPartialPayment?: boolean;
  onPostPayment?: (payload: {
    method: PaymentMethod;
    memo: string;
    emailReceipt: boolean;
    allocated: number;
    remaining: number;
  }) => void;
  onPaymentSuccess?: () => void;
}

const currency = (n: number) =>
  (isNaN(n) ? 0 : n).toLocaleString(undefined, { style: "currency", currency: "USD" });

export default function PaymentSection({
  selectedCount,
  selectedBalance,
  selectedOrders,
  allocatedAmount,
  remainingAmount,
  defaultEmailReceipt = true,
  defaultMethod = "card",
  selectedVendor,
  scope,
  company,
  teamMembers,
  offices,
  agents,
  bill_from,
  bill_to,
  reportId,
  variant = "default",
  cardOnly = false,
  maxAllowed: maxAllowedProp,
  allowPartialPayment = false,
  onPostPayment,
  onPaymentSuccess,
}: PaymentSectionProps) {
  const [method, setMethod] = useState<PaymentMethod>(defaultMethod);
  const [memo, setMemo] = useState("");
  const [emailReceipt, setEmailReceipt] = useState<boolean>(defaultEmailReceipt);
  const [enablePartial, setEnablePartial] = useState(false);
  const [partialAmount, setPartialAmount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { reportId: routeReportId } = useParams<{ reportId?: string }>();
  
  // Detect login type
  const isSignage = location.pathname.includes('/signage') && !location.pathname.includes('/signageteammember');
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const isRealtor = location.pathname.includes('/realtor');
  const isTeammember =
    location.pathname.includes('/teammember') && !location.pathname.includes('/signageteammember');
  const isAgent = location.pathname.includes('/agent');
  const isPublicPay = location.pathname.includes("/public/");
  const forceCardOnly =
    cardOnly || isRealtor || isTeammember || isAgent || isPublicPay;
  const showOfflineMethods = !forceCardOnly && (isSignage || isSignageTeammember);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryClientSecret = String(
    queryParams.get("client_secret") || queryParams.get("payment_intent_client_secret") || ""
  ).trim();
  const queryPublishableKey = String(
    queryParams.get("publishable_key") || queryParams.get("stripe_publishable_key") || ""
  ).trim();
  const querySupplierId = String(queryParams.get("supplier_id") || "").trim();
  
  // Stripe integration state
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<License>();
  
  // State for parent signage firm (for signageteammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  
  // Get user info
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const user = getAuthCookie("userName");
  
  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        
        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);
  
  // Force Card when public / realtor / teammember / agent
  useEffect(() => {
    if (forceCardOnly && method !== "card") {
      setMethod("card");
    }
  }, [forceCardOnly, method]);

  // Buyer logins charge the Bill From signage firm. Signage keeps its own user / parent firm.
  const vendorStripeId = String(selectedVendor?.supplier_id || "").trim();
  const isBuyerCardLogin = isRealtor || isTeammember || isAgent;
  const stripeUserId =
    querySupplierId ||
    (isBuyerCardLogin
      ? vendorStripeId
      : forceCardOnly && vendorStripeId
        ? vendorStripeId
        : isSignageTeammember && parentSignageId
          ? parentSignageId
          : userId);

  const maxAllowed = useMemo(
    () => Number(maxAllowedProp ?? selectedBalance ?? 0),
    [maxAllowedProp, selectedBalance]
  );

  const effectivePayAmount = useMemo(() => {
    if (method === "store_paid") return maxAllowed;
    if (allowPartialPayment && enablePartial && partialAmount > 0) {
      return Math.min(partialAmount, maxAllowed);
    }
    return maxAllowed;
  }, [allowPartialPayment, enablePartial, partialAmount, maxAllowed, method]);

  const ordersForPayment = useMemo(
    () => distributePartialAcrossOrders(selectedOrders, effectivePayAmount),
    [selectedOrders, effectivePayAmount]
  );

  const allocated = useMemo(
    () => (typeof allocatedAmount === "number" ? allocatedAmount : effectivePayAmount),
    [allocatedAmount, effectivePayAmount]
  );
  const remaining = useMemo(
    () =>
      typeof remainingAmount === "number"
        ? remainingAmount
        : Math.max(0, maxAllowed - effectivePayAmount),
    [remainingAmount, maxAllowed, effectivePayAmount]
  );

  const visibleOfflineMethods = useMemo(() => {
    const methods = [
      { key: "card" as PaymentMethod, label: "Card" },
      { key: "bank" as PaymentMethod, label: "Bank Transfer" },
      { key: "cheque" as PaymentMethod, label: "Cheque" },
      { key: "store_paid" as PaymentMethod, label: "Store Paid" },
    ];
    if (allowPartialPayment && enablePartial) {
      return methods.filter((m) => m.key !== "store_paid");
    }
    return methods;
  }, [allowPartialPayment, enablePartial]);

  const setPartialEnabled = (enabled: boolean) => {
    setEnablePartial(enabled);
    if (enabled && method === "store_paid") {
      setMethod("card");
    }
    if (!enabled) {
      setPartialAmount(0);
    }
  };

  const handlePartialAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    const clamped = Math.max(0, Math.min(numValue, maxAllowed));
    setPartialAmount(clamped);
  };

  const isOfflineMethod = method === "bank" || method === "cheque" || method === "store_paid";
  const disabled = selectedCount === 0
    || (method !== "card" && (isSignage || isSignageTeammember) && !memo.trim());

  // Fetch Stripe integrations (skipped on public pay when the email link already has Stripe keys)
  useEffect(() => {
    if (isPublicPay && queryPublishableKey) {
      return;
    }
    const fetchIntegrations = async () => {
      try {
        // For signageteammember, wait for parentSignageId to be available
        if (isSignageTeammember && !parentSignageId) {
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${stripeUserId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch integrations.");
        }
        const data = await response.json();
        setIntegrations(data);
      } catch (err) {
        console.error("Failed to load integrations:", err);
      }
    };

    if (stripeUserId && token) {
      fetchIntegrations();
    }
  }, [token, stripeUserId, isSignageTeammember, parentSignageId, isPublicPay, queryPublishableKey]);

  // Initialize Stripe
  useEffect(() => {
    if (isPublicPay && queryPublishableKey) {
      setStripePromise(loadStripe(queryPublishableKey));
      return;
    }
    const stripeInfo = integrations?.find((gateway: any) => gateway.gateway === "stripe");
    if (stripeInfo && stripeInfo.publishable_key) {
      const promise = loadStripe(stripeInfo.publishable_key);
      setStripePromise(promise);
    }
  }, [integrations, isPublicPay, queryPublishableKey]);

  // Create Payment Intent when Stripe is ready and method is card
  useEffect(() => {
    if (isPublicPay && queryClientSecret) {
      setClientSecret(queryClientSecret);
      return;
    }
    const fetchPaymentIntent = async () => {
      if (!stripePromise || method !== "card") return;

      // For signageteammember, wait for parentSignageId to be available
      if (isSignageTeammember && !parentSignageId) {
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${stripeUserId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              amount: Math.round(allocated * 100), // Convert to cents
              currency: "usd",
              payment_method_types: ["card"],
            }),
          }
        );
        const data = await response.json();
        setClientSecret(data.clientSecret || data.client_secret);
      } catch (err) {
        console.error("Failed to create PaymentIntent:", err);
      }
    };

    fetchPaymentIntent();
  }, [stripePromise, method, allocated, stripeUserId, token, isSignageTeammember, parentSignageId, isPublicPay, queryClientSecret]);

  // Handle batch payment API call
  const handleBatchPayment = async (paymentId: string) => {
    try {
      const mongoReportId = String(routeReportId || "").trim();
      const paymentUrl =
        isPublicPay && mongoReportId
          ? `${process.env.REACT_APP_API_URL}/public/batch-payments/reports/${encodeURIComponent(mongoReportId)}/payment`
          : `${process.env.REACT_APP_API_URL}/batch-payments/payments`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (!isPublicPay && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(paymentUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
            ...(reportId ? { report_id: reportId } : {}),
            scope: scope || "company",
            company: company || "",
            team_members: teamMembers || [],
            offices: offices || [],
            agents: agents || [],
            bill_from: bill_from || "",
            bill_to: bill_to || "",
            orders: ordersForPayment.map((order) => ({
              order_id: order.order_id,
              order_type: order.order_type,
              order_number: order.order_number || "",
              bill_to: order.bill_to || "",
            })),
            payment: {
              payment_id: paymentId,
              method: method === "card" ? "online" : method === "bank" ? "bank_transfer" : method === "store_paid" ? "store_paid" : "cheque",
              amount: effectivePayAmount,
              memo: memo.trim() || "",
              date: new Date().toISOString()
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to process batch payment");
      }

      const result = await response.json();
      showSuccess("Batch payment successful!");
      
      // Send email receipt if emailReceipt is true and user email exists
      if (!isPublicPay && emailReceipt && userInfo?.email && result._id) {
        try {
          await sendEmailReceipt(result._id, userInfo.email);
        } catch (emailError) {
          console.error("Failed to send email receipt:", emailError);
          // Don't fail the payment if email fails
        }
      }
      
      // Call success callback
      onPaymentSuccess?.();
      // navigate(0);
    } catch (error) {
      console.error("Batch payment failed:", error);
      throw error;
    }
    finally {
      setLoading(false);
      navigate(0);
    }
  };

  // Send email receipt function
  const sendEmailReceipt = async (transactionId: string, userEmail: string) => {
    try {
      const receiptPayload = {
        to: [userEmail,"srinivasselleklever2@gmail.com"],
        from: "support@sellklever.com",
        subject: "Your Payment Receipt",
        body: "<h2>Thank you for your payment!</h2><p>Please find your receipt attached.</p>",
        format: {
          header: {
            title: "Payment Receipt",
            date_format: "01/02/2006",
            title_color: "#000000",
            font_size: 16
          },
          sections: [
            {
              title: "Payment Information",
              title_color: "#000000",
              fields: [
                { "label": "Payer Scope", "value": "txn.PayerScope", "label_color": "#000000", "value_color": "#000000" },
                { "label": "Company", "value": "txn.Company", "label_color": "#000000", "value_color": "#000000" },
                { "label": "Team Members", "value": "txn.TeamMembers", "label_color": "#000000", "value_color": "#000000" },
                { "label": "Payment Method", "value": "txn.Method", "label_color": "#000000", "value_color": "#000000" },
                { "label": "Memo", "value": "txn.Payment.Memo", "label_color": "#000000", "value_color": "#000000" },
                { "label": "Total Amount", "value": "txn.TotalAmount", "label_color": "#000000", "value_color": "#000000" }
              ]
            }
          ],
          tables: [
            {
              title: "Orders Paid",
              title_color: "#000000",
              columns: [
                { "header": "Order ID", "value": "o.OrderNumber", "header_bg": "#ffffff", "header_fg": "#000000", "value_color": "#000000", "align": "L" },
                { "header": "Type", "value": "o.OrderType", "header_bg": "#ffffff", "header_fg": "#000000", "value_color": "#000000", "align": "L" },
                { "header": "Bill-To", "value": "o.BillTo", "header_bg": "#ffffff", "header_fg": "#000000", "value_color": "#000000", "align": "L" },
                { "header": "Amount", "value": "o.Amount", "header_bg": "#ffffff", "header_fg": "#000000", "value_color": "#000000", "align": "R" }
              ]
            }
          ]
        }
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/transactions/${transactionId}/receipt`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(receiptPayload)
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send email receipt");
      }

      console.log("Email receipt sent successfully");
    } catch (error) {
      console.error("Failed to send email receipt:", error);
      throw error;
    }
  };

  const post = async () => {
    if (disabled) return;

    try {
      // For offline methods (bank, cheque, store_paid) use memo as transaction ID
      const paymentId = memo.trim() || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Call batch payment API
      await handleBatchPayment(paymentId);
      
      // Call the post payment handler
      onPostPayment?.({ method, memo, emailReceipt, allocated, remaining });
    } catch (error) {
      console.error("Failed to process batch payment:", error);
      // Still call onPostPayment for UI updates even if batch payment fails
      onPostPayment?.({ method, memo, emailReceipt, allocated, remaining });
    }
  };

  useEffect(() => {
      const fetchLicense = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/users/${userId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
  
          if (!response.ok) {
            throw new Error("Failed to fetch license data");
          }
  
          const data = await response.json();
          // console.log("Create Signage Data:", data);
          if (data) {
            setUserInfo(data);
          }
  
        } catch (error) {
          console.error("Error fetching license data:", error);
        } finally {
          setLoading(false);
        }
      };
  
      fetchLicense();
    }, [userId, token]);

  return (
    <div className="card shadow-border-1 box-shadow-datatables p-2 bg-white mb-3">
      <div className="card-body">
        <div className="fw-bold mb-4 fs-18">Payment</div>

        {/* Create-batch only: selected order count + balance */}
        {variant === "default" && (
          <div className="row g-4 align-items-start mb-4">
            <div className="col-lg-4">
              <div className="fw-bold mb-2 fs-14">Selected</div>
              <div className="fs-14 mb-3">{selectedCount} orders</div>
            </div>
            <div className="col-lg-4">
              <div className="p-4 br-8 balance-box">
                <div className="fw-semibold mb-2">Selected Balance</div>
                <div className="fs-20 fw-bold balance-amount">{currency(selectedBalance)}</div>
              </div>
            </div>
          </div>
        )}

        {allowPartialPayment && (
          <div className="mb-4">
            <label className="d-flex align-items-center gap-2 mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={enablePartial}
                onChange={(e) => setPartialEnabled(e.target.checked)}
              />
              <span className="fw-semibold">Enable Partial Payment</span>
            </label>
            {enablePartial && (
              <div className="partial-box p-3 border rounded-3" style={{ maxWidth: 360, background: "#f9fbff" }}>
                <div className="mb-2 fw-semibold">Amount to Collect Now:</div>
                <input
                  type="number"
                  step="0.01"
                  className="form-control br-8"
                  value={partialAmount || ""}
                  onChange={(e) => handlePartialAmountChange(e.target.value)}
                  min={0}
                  max={maxAllowed}
                  placeholder="0"
                />
                <div className="mt-2 text-muted small">
                  Remaining Balance: {currency(remaining)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="row g-4 align-items-start mb-4">
          <div className={variant === "report" ? "" : "col-lg-4"}>
            <div className="mb-2 fw-semibold">Payment Method</div>
            <div className="d-flex align-items-center gap-4 flex-wrap">
              {/* All methods for private signage / signage teammember */}
              {showOfflineMethods &&
                visibleOfflineMethods.map((m) => (
                  <label key={m.key} className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="payment-method"
                      checked={method === m.key}
                      onChange={() => setMethod(m.key)}
                    />
                    {m.label}
                  </label>
                ))}

              {/* Card only: realtor / teammember / agent / public pay link */}
              {forceCardOnly && (
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    className="form-check-input"
                    name="payment-method"
                    checked={method === "card"}
                    onChange={() => setMethod("card")}
                  />
                  Card
                </label>
              )}
            </div>
          </div>

          <div className={`${variant === "report" ? "col-lg-6" : "col-lg-7"} d-flex align-items-end gap-3`}>
            <div className={variant === "report" ? "flex-grow-1" : "col-6"}>
              <div className="fw-bold mb-1">Payment Note</div>
              <input
                className="payer-form-control"
                placeholder="Payment note"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </div>
            <div className="col-auto mb-1 pb-1">
              <label className="form-check d-flex align-items-center gap-2 mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={emailReceipt}
                  onChange={(e) => setEmailReceipt(e.target.checked)}
                />
                Email receipt
              </label>
            </div>
          </div>
        </div>

        {/* Stripe Elements - Only show when Card is selected */}
        {method === "card" && stripePromise && clientSecret && (
          <div className="row mb-4">
            <div className={variant === "report" ? "col-12 col-lg-6" : "col-12 col-lg-3"}>
              <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                <PaymentFormManualOrder
                  url="batch-payments"
                  method="POST"
                  supplierId={stripeUserId || ""}
                  totalAmount={maxAllowed}
                  paidAmount={effectivePayAmount}
                  orderId="batch-payment"
                  selectedPaymentMethod="online"
                  hideSavedCards
                  onPaymentSuccess={async (paymentIntentId) => {
                    console.log("Payment successful:", paymentIntentId);
                    try {
                      await handleBatchPayment(paymentIntentId);
                      onPostPayment?.({ method, memo, emailReceipt, allocated, remaining });
                    } catch (error) {
                      console.error("Failed to process batch payment:", error);
                      onPostPayment?.({ method, memo, emailReceipt, allocated, remaining });
                    }
                  }}
                  userId={userId || ""}
                  userEmail={userInfo?.email || ""}
                  paidBy_id={userId || ""}
                  paidBy_name={user || ""}
                  payments_status="pending"
                />
              </Elements>
            </div>
          </div>
        )}

        {/* Post Payment Button - Only show for Bank Transfer, Cheque, Store Paid (private signage only) */}
        {isOfflineMethod && showOfflineMethods && (
          <div className="row d-flex align-items-end justify-content-end">
            <div className="col-lg-3 d-flex align-items-end justify-content-end">
              <button
                className={`btn btn-primary table-addbutton px-4 py-2 ${disabled ? "btn-disabled" : ""}`}
                disabled={disabled || loading}
                onClick={post}
              >
                {loading ? "Processing..." : "Post Payment"}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* New balance panel styles */
        .balance-box { background:#eff6ff; border:2px solid #bedbff; }
        .balance-amount { color:#1447e6; }

        .btn-disabled{opacity:.6; cursor:not-allowed}
        .payer-form-control{width:100%;height:39px;border-radius:10px;padding:10px 12px;border:2px solid #c9ced6;background:#f5f6fa;outline:none;transition:all .2s;color:#111827}
        .payer-form-control:hover{border-color:#9ca3af;background:#fafafa}
        .payer-form-control:focus{border-color:#9ca3af;background:#fafafa;box-shadow:none}
      `}</style>
    </div>
  );
}